import { useEffect, useState } from 'react'
import { salesService } from '../../services/api'
import { useAuth } from '../auth/AuthProvider'
import type { SalesSetting } from './types'

interface InitialData {
  branches: any[]
  staff: any[]
  salesSettings: SalesSetting[]
  accountGuideMap: Record<string, any>
  productNames: any[]
  currencies: any[]
  paymentMethods: any[]
}

interface DefaultsOut {
  def_branch: number
  staff_user_id: number
  seller: string
  branch: string
  statement: string
}

export function useInvoiceInitialData() {
  const { isAuthenticated, token } = useAuth()

  const [loadingInit, setLoadingInit] = useState(false)
  const [data, setData] = useState<InitialData>({
    branches: [],
    staff: [],
    salesSettings: [],
    accountGuideMap: {},
    productNames: [],
    currencies: [],
    paymentMethods: []
  })
  const [defaults, setDefaults] = useState<DefaultsOut | null>(null)

  useEffect(() => {
    if (!isAuthenticated || !token) return

    const load = async () => {
      try {
        setLoadingInit(true)
        // Get selected branch from homepage dropdown (localStorage)
        const selectedBranch = localStorage.getItem('selectedBranch')
        const def_branch = selectedBranch ? parseInt(selectedBranch, 10) : 1
        console.log('🏢 Using branch from homepage selection:', def_branch)
        
        const res = await salesService.getInitialData({ def_branch, begin_screen: true })
        const api: any = res || {}

        const branches = api.branches || []
        const staff = api.staff || []
        const salesSettings = api.sales_setting || []
        const accountGuideMap = (api.account_guide_key_id as any) || {}
        const productNames = api.product_name || []
        const currencies = api.currencies || []
        const paymentMethods = api.pay_method || []

        setData({
          branches,
          staff,
          salesSettings,
          accountGuideMap,
          productNames,
          currencies,
          paymentMethods
        })
        // Respect the branch selected from the homepage/localStorage
        const defaultBranchId = def_branch || ((branches && branches[0]?.id) ? Number(branches[0].id) : 1)
        const defaultStaffId = api.staff_selected ? Number(api.staff_selected) : 0
        const defaultStaffName = (staff || []).find((u: any) => Number(u.id) === defaultStaffId)?.name_ar
          || (staff || []).find((u: any) => Number(u.id) === defaultStaffId)?.name
          || 'البائع'

        const cashVisible = (salesSettings || []).filter((s: any) => s.is_cash_visible)
        const agelVisible = (salesSettings || []).filter((s: any) => s.is_agel_visible)
        const defaultStatement = (cashVisible[0]?.cash_name_ar) || (agelVisible[0]?.agel_name_ar) || ''

        // Determine selected branch display name based on the selected ID
        const selectedBranchObj = (branches || []).find((b: any) => Number(b.id) === defaultBranchId) || branches[0]
        const selectedBranchName = (selectedBranchObj?.name_a_original || selectedBranchObj?.name_a || selectedBranchObj?.name) || 'الفرع الرئيسي'

        setDefaults({
          def_branch: defaultBranchId,
          staff_user_id: defaultStaffId,
          seller: defaultStaffName,
          branch: selectedBranchName,
          statement: defaultStatement
        })
      } catch (e) {
        if (process.env.NODE_ENV !== 'production') {
          // eslint-disable-next-line no-console
          console.error('Failed to load initial data:', e)
        }
      } finally {
        setLoadingInit(false)
      }
    }

    load()
  }, [isAuthenticated, token])

  return {
    isAuthenticated,
    loadingInit,
    ...data,
    defaults
  }
}
